import React, { useState } from 'react';
import { 
  Key, ShieldCheck, Database, Radio, 
  Lock, AlertTriangle, CheckCircle2 
} from 'lucide-react';
import { SecurityOverviewHeader } from './SecurityOverviewHeader';
import { AuthenticationSubTab } from './AuthenticationSubTab';
import { AuthorizationSubTab } from './AuthorizationSubTab';
import { DataProtectionSubTab } from './DataProtectionSubTab';
import { ThreatProtectionSubTab } from './ThreatProtectionSubTab';
import toast from 'react-hot-toast';

interface SecurityKernelProps {
  initialSubTab?: 'auth' | 'abac' | 'data-protection' | 'threat';
}

export function SecurityKernel({ initialSubTab = 'auth' }: SecurityKernelProps) {
  const [activeSection, setActiveSection] = useState<'auth' | 'abac' | 'data-protection' | 'threat'>(initialSubTab);

  const sections = [
    {
      id: 'auth' as const,
      label: 'Authentication',
      sub: 'SSO, MFA, Passwords',
      icon: <Key className="size-5" />,
    },
    {
      id: 'abac' as const,
      label: 'Authorization',
      sub: 'ABAC, Contextual Grants',
      icon: <Lock className="size-5" />,
    },
    {
      id: 'data-protection' as const,
      label: 'Data Protection',
      sub: 'Encryption, Masking, DLP',
      icon: <Database className="size-5" />,
    },
    {
      id: 'threat' as const,
      label: 'Threat Protection',
      sub: 'IP, Geo-Fence, Anomaly',
      icon: <Radio className="size-5" />,
    },
  ];

  return (
    <div className="min-h-full bg-background text-foreground p-4 space-y-4 pb-12 animate-in fade-in">
      {/* 1. Cross-Cutting Overview Header with Live Status Chips */}
      <SecurityOverviewHeader
        onSaveAll={() => toast.success('All security configurations synced and committed')}
        activeSubTabTitle={sections.find((s) => s.id === activeSection)?.label}
      />

      {/* 2. Primary 4 Sub-Tabs Navigation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {sections.map((s) => {
          const isSelected = activeSection === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveSection(s.id)}
              className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                isSelected
                  ? 'border-primary bg-primary/5 text-primary shadow-xs ring-1 ring-primary/20'
                  : 'border-border bg-card hover:border-primary/40 text-foreground'
              }`}
            >
              <div className={`size-8 rounded-xl flex items-center justify-center mb-2.5 ${
                isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {s.icon}
              </div>
              <p className="font-semibold text-xs sm:text-sm text-foreground">{s.label}</p>
              <p className="text-3xs sm:text-xs text-muted-foreground mt-0.5">{s.sub}</p>
            </button>
          );
        })}
      </div>

      {/* 3. Sub-Tab Content Rendering */}
      <div className="pt-1">
        {activeSection === 'auth' && <AuthenticationSubTab />}
        {activeSection === 'abac' && <AuthorizationSubTab />}
        {activeSection === 'data-protection' && <DataProtectionSubTab />}
        {activeSection === 'threat' && <ThreatProtectionSubTab />}
      </div>
    </div>
  );
}
